import { useMemo, useState } from 'react';

import { useAuditFacets, useAuditLog, useAuditSummary } from '@/api/queries';
import type { AuditFilters } from '@/api/types';
import { dateBounds, type DateFilterValue } from '@/ui/DateFilter';

import { useAuditTimeZone } from './auditTime';

export const DEFAULT_DATE: DateFilterValue = { kind: 'preset', preset: '7d' };

interface Refinable {
  date: DateFilterValue;
  actors: string[];
  actions: string[];
  outcomes: string[];
  entityTypes: string[];
  sourceIp: string;
  /** Show the reads recorded while tracing was on (hidden by default). */
  traced: boolean;
}

/** Whether anything narrows the list beyond the default last 7 days. */
export function isFiltered(f: Refinable): boolean {
  const defaultDate = f.date.kind === 'preset' && f.date.preset === '7d';
  const lists = [f.actors, f.actions, f.outcomes, f.entityTypes].some((list) => list.length > 0);
  return lists || Boolean(f.sourceIp) || f.traced || !defaultDate;
}

/** What the queries are asked for. */
function useAuditFilters(f: Refinable, utc: boolean, order: 'asc' | 'desc'): AuditFilters {
  const { date, actors, actions, outcomes, entityTypes, sourceIp, traced } = f;
  // Rolling ranges count back from when they were picked (or the zone flipped).
  const bounds = useMemo(() => dateBounds(date, utc), [date, utc]);
  return useMemo<AuditFilters>(
    () => ({
      occurred_from: bounds.from,
      occurred_to: bounds.to,
      actor: actors,
      action: actions,
      outcome: outcomes,
      entity_type: entityTypes,
      source_ip: sourceIp,
      traced,
      order,
    }),
    [bounds, actors, actions, outcomes, entityTypes, sourceIp, traced, order],
  );
}

/** The audit log's filters, keyset paging and expanded row, and the queries they drive. */
export function useAuditLogState() {
  const facets = useAuditFacets();
  const [utc, setUtc] = useAuditTimeZone();
  const [date, setDate] = useState<DateFilterValue>(DEFAULT_DATE);
  const [actors, setActors] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [sourceIp, setSourceIp] = useState('');
  const [traced, setTraced] = useState(false);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState(50);
  // The cursor that opened each page so far: page N is cursors[N - 1], and
  // Previous pops back to a cursor already seen.
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const filters = useAuditFilters({ date, actors, actions, outcomes, entityTypes, sourceIp, traced }, utc, order);
  const page = cursors.length;
  const log = useAuditLog(filters, cursors[page - 1] ?? null, pageSize);
  const summary = useAuditSummary(filters);
  const entries = log.data?.entries ?? [];
  const filtered = isFiltered({ date, actors, actions, outcomes, entityTypes, sourceIp, traced });

  // Any change to what matches, or to its order, starts again at page 1.
  function refine<T>(set: (value: T) => void) {
    return (value: T) => {
      set(value);
      setCursors([null]);
    };
  }

  function goTo(next: number) {
    const after = log.data?.next_cursor;
    if (next < page) setCursors((c) => c.slice(0, next));
    else if (after) setCursors((c) => [...c, after]);
  }

  function clear() {
    setDate(DEFAULT_DATE);
    setActors([]);
    setActions([]);
    setOutcomes([]);
    setEntityTypes([]);
    setSourceIp('');
    setTraced(false);
    setCursors([null]);
  }

  return {
    facets: facets.data,
    utc,
    // The time zone moves the date bounds, so it re-filters from page 1 like any other filter.
    setUtc: refine(setUtc),
    values: { date, actors, actions, outcomes, entityTypes, sourceIp, traced },
    set: {
      date: refine(setDate),
      actors: refine(setActors),
      actions: refine(setActions),
      outcomes: refine(setOutcomes),
      entityTypes: refine(setEntityTypes),
      sourceIp: refine(setSourceIp),
      traced: refine(setTraced),
      pageSize: refine(setPageSize),
    },
    order,
    flipOrder: () => {
      setOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
      setCursors([null]);
    },
    pageSize,
    page,
    goTo,
    clear,
    filtered,
    log,
    summary,
    entries,
    expanded,
    toggle: (id: string) => setExpanded((current) => (current === id ? null : id)),
  };
}

export type AuditLogState = ReturnType<typeof useAuditLogState>;
